import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { useDispatch, useSelector } from "react-redux";
import { ActivitiesApi } from "Data/ActivitiesApi";
import { ActivitiesActions } from "Store/Activities/Activities.actions";
import { IUrnFilenameMap, ViewpointCaptureService } from "Services/ViewpointCaptureService";
import { IViewpointEngine } from "Services/viewpoint/IViewpointEngine";
import { IInitialViewpoint } from "Data/interfaces/Activities/IViewpointData";
import { ITopicCreateRequest } from "Data/interfaces/Activities/ITopicCreateRequest";
import { ICustomOption } from "Pages/Activities/Modals/ShowCreateActivity/useOptionsSelect";
import { IStage } from "Data/interfaces/Obra/IObraStages";
import toastHandler from "Utils/toastHandler";
import { IGlobalReducerState } from "Store/Base/interface/IGlobalReducerState";

interface IUseCreateActivityWithViewpoint {
  visible: boolean;
  csId: number;
  engine: IViewpointEngine | null;
  typeOptions: ICustomOption[];
  stages: IStage[];
  allowOnlyStagesLinkedToDisciplinesInTopics: boolean;
  onClose: () => void;
}

export interface ICreateActivityFormData {
  title: string;
  description: string | null;
  typeFk?: number;
  constructionSiteDisciplinesFk?: number[];
  statusFk?: number;
  assignedUsersFk?: string[];
  dueDate: Date | null;
  visibility: number;
  priority: number | null;
  stageFk?: string;
  pointsFk?: string[];
  communicationTypeFk?: string;
  newPoints?: string[];
  newCommunicationType?: string;
  newStage?: string;
  ConstructionSiteFk: number;
}

const useCreateActivityWithViewpoint = ({
  visible,
  csId,
  engine,
  typeOptions,
  stages,
  allowOnlyStagesLinkedToDisciplinesInTopics,
  onClose,
}: IUseCreateActivityWithViewpoint) => {
  const { t } = useTranslation('federatedViewer');
  const dispatch = useDispatch();
  
  // Get file data from Redux to build URN to filename mapping
  const viewerListDisciplines = useSelector((state: IGlobalReducerState) => state.federatedViewer.viewerListDisciplines);
  const {
    topicPointsOptions = [],
    topicStatusOptions = [],
    topicStagesOptions = [],
    topicCommunicationTypesOptions = [],
  } = useSelector((state: IGlobalReducerState) => state.activities);

  const [loading, setLoading] = useState(false);
  const [capturingViewpoint, setCapturingViewpoint] = useState(false);
  const [viewpointData, setViewpointData] = useState<IInitialViewpoint | null>(null);
  const [snapshotUrl, setSnapshotUrl] = useState<string | undefined>(undefined);
  const [isSubmit, setIsSubmit] = useState(false);
  const [titleValue, setTitleValue] = useState("");
  const [selectedDisciplines, setSelectedDisciplines] = useState<number[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [defaultValues, setDefaultValues] = useState<ICreateActivityFormData>({} as ICreateActivityFormData);

  // Build URN to filename map from Redux state
  // This ensures we use the actual filenames instead of "Unknown"
  const buildUrnFilenameMap = useCallback((): IUrnFilenameMap => {
    const map: IUrnFilenameMap = {};
    
    // Iterate through all disciplines and their files
    viewerListDisciplines.forEach(discipline => {
      discipline.FilesList?.forEach(file => {
        const urn = file.FileInfo?.BucketFile?.Urn;
        const filename = file.Name;
        
        if (urn && filename) {
          // Store with "urn:" prefix to match what the viewer uses
          map[`urn:${urn}`] = filename;
          // Also store without prefix in case viewer returns it without
          map[urn] = filename;
        }
      });
    });
    
    return map;
  }, [viewerListDisciplines]);

  // Capture viewpoint when modal opens
  useEffect(() => {
    const captureViewpoint = async () => {
      if (visible && engine && !viewpointData) {
        setCapturingViewpoint(true);
        try {
          // O mapa de urn→nome alimenta a captura da Autodesk; o motor ifc-lite
          // já sabe o nome de cada modelo pelo contexto que recebe.
          ViewpointCaptureService.setUrnFilenameMap(buildUrnFilenameMap());
          const captured = await engine.capture();
          setViewpointData(captured);
          setSnapshotUrl(captured.SnapshotBase64);
        } catch (error) {
          console.error("Error capturing viewpoint:", error);
          toastHandler.showError(t('captureError'));
        } finally {
          setCapturingViewpoint(false);
        }
      }
    };

    captureViewpoint();
  }, [visible, engine, viewpointData, buildUrnFilenameMap]);

  // Initialize default values when modal opens
  useEffect(() => {
    if (visible && typeOptions.length > 0) {
      const defaultStatus = topicStatusOptions.find(status => status.Order === 1) || topicStatusOptions[0];

      setDefaultValues({
        title: "",
        description: null,
        typeFk: Number(typeOptions[0].id),
        constructionSiteDisciplinesFk: [],
        assignedUsersFk: [],
        dueDate: null,
        visibility: 1,
        priority: null,
        stageFk: undefined,
        pointsFk: [],
        communicationTypeFk: undefined,
        ConstructionSiteFk: csId,
        statusFk: defaultStatus?.TopicStatusId,
      });
    }
  }, [visible, csId, typeOptions, topicStatusOptions]);

  // Reset state when modal closes
  useEffect(() => {
    if (!visible) {
      setViewpointData(null);
      setSnapshotUrl(undefined);
      setTitleValue("");
      setSelectedDisciplines([]);
      setErrors([]);
      setIsSubmit(false);
      setDefaultValues({} as ICreateActivityFormData);
    }
  }, [visible]);

  const handleTitleValue = useCallback((value: string) => {
    setTitleValue(value);
    if (errors.includes("title")) {
      setErrors(prev => prev.filter(e => e !== "title"));
    }
  }, [errors]);

  const handleSelectedDisciplines = useCallback((disciplines: number[]) => {
    setSelectedDisciplines(disciplines);
    if (errors.includes("discipline")) {
      setErrors(prev => prev.filter(e => e !== "discipline"));
    }
  }, [errors]);

  const handleCustomError = useCallback((errorType: string) => {
    setErrors(prev => {
      if (!prev.includes(errorType)) {
        return [...prev, errorType];
      }
      return prev;
    });
  }, []);

  const handleClearCustomError = useCallback((errorType: string) => {
    setErrors(prev => prev.filter(e => e !== errorType));
  }, []);

  const submit = async (formData?: ICreateActivityFormData, validationErrors?: string[]) => {
    // If validation errors exist, form schema validation failed
    if (!formData) {
      setLoading(false);
      setIsSubmit(false);
      return;
    }

    if (!isSubmit) return;

    setLoading(true);

    try {
      // Additional custom validation using state values (like original ShowCreateActivity)
      let hasErrors = false;

      if (!titleValue) {
        handleCustomError("title");
        hasErrors = true;
      }
      if (!formData.constructionSiteDisciplinesFk || formData.constructionSiteDisciplinesFk.length === 0) {
        handleCustomError("discipline");
        hasErrors = true;
      }
      // Sanitize point ids to remove invalid entries coming from multi-select controls.
      const sanitizedPoints = (formData.pointsFk || []).filter((point): point is string => {
        if (point === null || point === undefined) {
          return false;
        }

        const value = String(point).trim();
        return value.length > 0 && value.toLowerCase() !== "null";
      });

      if (sanitizedPoints.length === 0) {
        handleCustomError("point");
        hasErrors = true;
      }

      if (formData.dueDate && new Date(formData.dueDate) < new Date()) {
        handleCustomError("date");
        hasErrors = true;
      }

      if (hasErrors) {
        setLoading(false);
        setIsSubmit(false);
        toastHandler.showError(i18n.t('messages:toasts.fillRequiredFields'));
        return;
      }

      // Convert point labels into ids, mirroring the legacy creation flow so the API always receives numbers.
      const existingPointIds: number[] = [];
      const pendingNewPoints: string[] = [];

      sanitizedPoints.forEach((pointLabel) => {
        const matchedPoint = topicPointsOptions.find(
          (point) => pointLabel.toLowerCase() === point.Name.toLowerCase()
        );

        if (matchedPoint) {
          existingPointIds.push(matchedPoint.TopicPointId);
        } else {
          pendingNewPoints.push(pointLabel);
        }
      });

      const defaultStatus = topicStatusOptions.find(status => status.Order === 1) || topicStatusOptions[0];
      if (!defaultStatus) {
        toastHandler.showError(i18n.t('messages:toasts.noDefaultStatus'));
        setLoading(false);
        setIsSubmit(false);
        return;
      }

      // Resolve stage selection similar to the classic activity form.
      let resolvedStageFk: number | undefined;
      let resolvedNewStage: string | undefined;
      const rawStageValue = formData.stageFk
        ? (Array.isArray(formData.stageFk) ? formData.stageFk[0] : formData.stageFk)
        : undefined;

      if (rawStageValue) {
        if (allowOnlyStagesLinkedToDisciplinesInTopics && stages.length > 0) {
          const filteredStages = stages.filter((stage) => {
            return (
              stage.IsDefault ||
              stage.ConstructionSiteStageDisciplines.some((discipline) =>
                formData.constructionSiteDisciplinesFk?.includes(discipline.ConstructionSiteDisciplineFk)
              )
            );
          });

          const stageRecords = filteredStages.reduce<Record<string, number>>((acc, stage) => {
            const key = `${stage.Acronym || ""}${stage.Acronym ? " | " : ""}${stage.Name}`;
            acc[key] = stage.TopicStageId;
            return acc;
          }, {});

          const matchedStageId = stageRecords[rawStageValue];
          if (matchedStageId) {
            resolvedStageFk = matchedStageId;
          }
        } else {
          const matchedStage = topicStagesOptions.find(
            (stage) => rawStageValue.toLowerCase() === stage.Name.toLowerCase()
          );
          if (matchedStage) {
            resolvedStageFk = matchedStage.TopicStageId;
          } else {
            resolvedNewStage = rawStageValue;
          }
        }
      }

      // Resolve communication type selection similar to the classic activity form.
      let resolvedCommunicationTypeFk: number | undefined;
      let resolvedNewCommunicationType: string | undefined;
      const rawCommunicationValue = formData.communicationTypeFk
        ? (Array.isArray(formData.communicationTypeFk) ? formData.communicationTypeFk[0] : formData.communicationTypeFk)
        : undefined;

      if (rawCommunicationValue) {
        const matchedCommunication = topicCommunicationTypesOptions.find(
          (communication) => rawCommunicationValue.toLowerCase() === communication.Name.toLowerCase()
        );

        if (matchedCommunication) {
          resolvedCommunicationTypeFk = matchedCommunication.TopicCommunicationTypeId;
        } else {
          resolvedNewCommunicationType = rawCommunicationValue;
        }
      }

      // Filter out empty assigned users to keep payload clean.
      const normalizedAssignedUsers = (formData.assignedUsersFk || []).filter((userId) => !!userId);

      // Prepare payload with viewpoint data (use titleValue from state like original)
      const payload: ITopicCreateRequest = {
        title: titleValue,
        description: formData.description,
        typeFk: formData.typeFk,
        priority: formData.priority,
        visibility: formData.visibility,
        dueDate: formData.dueDate,
        statusFk: Number(defaultStatus.TopicStatusId),
        assignedUsersFk: normalizedAssignedUsers.length ? normalizedAssignedUsers : undefined,
        constructionSiteDisciplinesFk: formData.constructionSiteDisciplinesFk,
        stageFk: resolvedStageFk,
        newStage: resolvedNewStage,
        communicationTypeFk: resolvedCommunicationTypeFk,
        newCommunicationType: resolvedNewCommunicationType,
        pointsFk: existingPointIds.length > 0 ? existingPointIds : undefined,
        newPoints: pendingNewPoints.length > 0 ? pendingNewPoints : undefined,
        ConstructionSiteFk: csId,
        initialViewpoint: viewpointData || undefined,
      };

      // Create activity with viewpoint
      const response = await ActivitiesApi.createActivity(payload);

      if (response.data) {
        toastHandler.showSuccess(i18n.t('messages:toasts.activityCreatedBang'));

        // Refresh activities list
        dispatch(ActivitiesActions.listActivities({ csId }));

        // Close modal
        onClose();
      }
    } catch (error) {
      console.error("Error creating activity with viewpoint:", error);
      toastHandler.showError(i18n.t('messages:toasts.createActivityError'));
    } finally {
      setLoading(false);
      setIsSubmit(false);
    }
  };

  const handleIsSubmit = useCallback((value: boolean) => {
    setIsSubmit(value);
  }, []);

  return {
    loading,
    capturingViewpoint,
    snapshotUrl,
    isSubmit,
    titleValue,
    selectedDisciplines,
    errors,
    defaultValues,
    handleTitleValue,
    handleSelectedDisciplines,
    handleCustomError,
    handleClearCustomError,
    handleIsSubmit,
    submit,
  };
};

export default useCreateActivityWithViewpoint;
